import { useCallback, useEffect, useRef } from 'react';
import UsageTracker from '../../../utils/UsageTracker';
import { parseJSONOrReturn } from '../../../utils/utils';
const USAGE_TRACKER_EVENT = 'usageTrackerEvent';
const ACADEMY_LABS_SESSION_IFRAME_ID = 'academy-labs-session-iframe';
const SELF_EVENT_NAMES = ['practiceModeInteraction', 'Practice Mode Interaction'];
export const RESCAN_DEBOUNCE_MS = 200;
const safeGet = fn => {
  try {
    return fn();
  } catch (_unused) {
    return null;
  }
};
const formatSegment = str => {
  if (!(str !== null && str !== void 0 && str.trim())) {
    return undefined;
  }
  const formatted = str.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/[_\W]+/g, ' ').trim().toLowerCase().split(/\s+/).map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  return formatted || undefined;
};
const formatUsageEventLabel = eventPayload => {
  const namespace = formatSegment(eventPayload.where_app);
  const name = formatSegment(eventPayload.what_event);
  if (!namespace || !name) {
    return undefined;
  }
  return `[${namespace}] ${name}`;
};
const parseWhatExtraJsonObject = payload => {
  const raw = payload === null || payload === void 0 ? void 0 : payload.what_extra_json;
  if (raw == null) {
    return null;
  }
  if (typeof raw === 'object') {
    return raw;
  }
  if (typeof raw !== 'string') {
    return null;
  }
  return parseJSONOrReturn(raw);
};
const mutationsAddIframe = mutations => mutations.some(m => Array.from(m.addedNodes).some(node => {
  const el = node;
  if (el.tagName === 'IFRAME') return true;
  return typeof el.querySelector === 'function' ? el.querySelector('iframe') != null : false;
}));
export const useAcademyLabsSessionUsageTracking = ({
  originPortalId,
  skip = false,
  simulationPortalId
} = {}) => {
  const portalIdRef = useRef(simulationPortalId);
  portalIdRef.current = simulationPortalId;
  const receiveUsageEvent = useCallback(event => {
    var _detail;
    const {
      eventPayload
    } = (_detail = event.detail) !== null && _detail !== void 0 ? _detail : {};
    const parsedExtra = parseWhatExtraJsonObject(eventPayload);
    const whatExtraRecord = parsedExtra && typeof parsedExtra === 'object' && !Array.isArray(parsedExtra) ? parsedExtra : undefined;
    if (!eventPayload || SELF_EVENT_NAMES.includes(eventPayload.what_event)) {
      return;
    }
    const eventName = formatUsageEventLabel(eventPayload);
    if (!eventName) return;
    const screenHint = eventPayload.where_screen;
    const actionStr = typeof (whatExtraRecord === null || whatExtraRecord === void 0 ? void 0 : whatExtraRecord.action) === 'string' ? whatExtraRecord.action : undefined;
    const deployedStr = typeof (whatExtraRecord === null || whatExtraRecord === void 0 ? void 0 : whatExtraRecord.deployableName) === 'string' ? whatExtraRecord.deployableName : undefined;
    UsageTracker.trackStandalone('Practice Mode Interaction', Object.assign({
      academySimulationPortalId: portalIdRef.current,
      eventName,
      usageType: 'academyLabs'
    }, originPortalId != null && originPortalId !== '' ? {
      portal: originPortalId
    } : {}, actionStr != null ? {
      action: actionStr
    } : {}, deployedStr != null ? {
      deployedEnvironment: deployedStr
    } : {}, screenHint ? {
      screen: screenHint
    } : {}));
  }, [originPortalId]);
  const startTracking = useCallback(({
    iframe
  }) => {
    if (!iframe || skip) return undefined;

    //Responsible for cleaning up the event listeners attached to the windows
    const toCleanup = [];
    const attach = w => {
      if (w === window) return;
      const childTarget = safeGet(() => w.parent);
      if (!childTarget) return;
      try {
        childTarget === null || childTarget === void 0 || childTarget.addEventListener(USAGE_TRACKER_EVENT, receiveUsageEvent);
        toCleanup.push(childTarget);
      } catch (_unused2) {
        // cross-origin or torn-down target; skip
      }
    };

    // Scan for iframes and attaches listeners to the parent window of the iframe
    const scanIframes = doc => {
      doc.querySelectorAll('iframe').forEach(iFrame => {
        const cw = safeGet(() => iFrame.contentWindow);
        const innerDoc = safeGet(() => iFrame.contentDocument);
        if (cw && innerDoc !== null && innerDoc !== void 0 && innerDoc.body) {
          attach(cw);
          scanIframes(innerDoc);
        }
      });
    };
    const scan = () => {
      const cw = safeGet(() => iframe.contentWindow);
      const cd = safeGet(() => iframe.contentDocument);
      if (cw) attach(cw);
      if (cd) scanIframes(cd);
    };
    let debounceTimer;
    let observer;
    const doc = safeGet(() => {
      var _iframe$contentWindow;
      return (_iframe$contentWindow = iframe.contentWindow) === null || _iframe$contentWindow === void 0 ? void 0 : _iframe$contentWindow.document;
    });
    if (doc !== null && doc !== void 0 && doc.body) {
      scan();
      observer = new MutationObserver(mutations => {
        if (!mutationsAddIframe(mutations)) return;
        clearTimeout(debounceTimer);
        //If the adds new iframe via CTA we scan for new iframes and attach listeners to the parent window of the new iframe
        debounceTimer = setTimeout(scan, RESCAN_DEBOUNCE_MS);
      });
      observer.observe(doc, {
        childList: true,
        subtree: true
      });
    }
    const dispose = () => {
      var _observer;
      clearTimeout(debounceTimer);
      (_observer = observer) === null || _observer === void 0 || _observer.disconnect();
      toCleanup.forEach(target => safeGet(() => target.removeEventListener(USAGE_TRACKER_EVENT, receiveUsageEvent)));
    };
    return dispose;
  }, [receiveUsageEvent, skip]);

  // Auto-start tracking as soon as the labs iframe is mounted and loaded,
  // so callers don't need to wire up an onLoad handler.
  useEffect(() => {
    if (skip || typeof document === 'undefined') return undefined;
    let watchedIframe = null;
    let onIframeLoad;
    let domObserver;
    const start = iframe => {
      startTracking({
        iframe
      });
    };
    const watchIframe = iframe => {
      watchedIframe = iframe;
      if (safeGet(() => {
        var _iframe$contentDocume;
        return (_iframe$contentDocume = iframe.contentDocument) === null || _iframe$contentDocume === void 0 ? void 0 : _iframe$contentDocume.readyState;
      }) === 'complete') {
        start(iframe);
      }
      onIframeLoad = () => start(iframe);
      iframe.addEventListener('load', onIframeLoad);
    };
    const existing = document.getElementById(ACADEMY_LABS_SESSION_IFRAME_ID);
    if (existing instanceof HTMLIFrameElement) {
      watchIframe(existing);
    } else {
      //Waits for the iframe to be added to the DOM
      domObserver = new MutationObserver(() => {
        const el = document.getElementById(ACADEMY_LABS_SESSION_IFRAME_ID);
        if (el instanceof HTMLIFrameElement) {
          var _domObserver;
          (_domObserver = domObserver) === null || _domObserver === void 0 || _domObserver.disconnect();
          domObserver = undefined;
          watchIframe(el);
        }
      });
      domObserver.observe(document.body, {
        childList: true,
        subtree: true
      });
    }
    return () => {
      var _domObserver2;
      (_domObserver2 = domObserver) === null || _domObserver2 === void 0 || _domObserver2.disconnect();
      if (watchedIframe && onIframeLoad) {
        watchedIframe.removeEventListener('load', onIframeLoad);
      }
    };
  }, [skip, startTracking]);
  return {
    startTracking
  };
};